import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLines, Reveal, SectionHead } from "@/components/site/Motion";
import { TripCard } from "@/components/site/TripCard";
import { PlacesExplorer } from "@/components/home/GeorgiaSpotlight";
import { CallbackSection } from "@/components/home/CallbackSection";
import { useEnquiry } from "@/context/EnquiryContext";
import { useTrips } from "@/hooks/useTrips";
import { IMG } from "@/data/site";
import { TrustBadges } from "@/components/site/TrustBadges";
import { RefusalStory } from "@/components/georgia/RefusalStory";
import { EImg, T } from "@/context/ContentContext";
import { useSeo } from "@/hooks/useSeo";

const REASONS = [
  ["Mountains & monasteries", "Kazbegi, Gudauri and the Caucasus, with churches perched on ridgelines."],
  ["The cradle of wine", "Kakheti's qvevri cellars and long Georgian supras with locals."],
  ["Black Sea summers", "Batumi's boulevard, beaches and late sunsets."],
  ["Visa & planning help", "Our team guides you through the paperwork before you fly."],
];

function GeorgiaHero() {
  const ref = useRef(null);
  const { openEnquiry } = useEnquiry();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden bg-wine-deep text-cream" data-testid="georgia-hero">
      <EImg place="top-28 right-5" as={motion.img} k="georgia.hero.img" style={{ y, scale }} src={IMG.kazbegi} alt="Kazbegi, Georgia" className="absolute inset-0 h-full w-full object-cover opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-t from-wine-deep via-wine-deep/40 to-transparent pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 min-h-[100svh] flex flex-col justify-end pb-20 pt-32">
        <T k="georgia.hero.eyebrow" as="p" className="eyebrow text-gold mb-6">Trippy Tastic · Georgia specialists</T>
        <h1 className="font-display font-extrabold tracking-[-0.05em] leading-[0.88] text-6xl sm:text-8xl lg:text-[9rem]">
          <MaskLines lines={[<T k="georgia.hero.title.0">Georgia,</T>, <T k="georgia.hero.title.1" className="font-serif italic font-normal tracking-[-0.02em] text-gold">our home turf.</T>]} />
        </h1>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.9 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 max-w-3xl">
          <T k="georgia.hero.intro" as="p" className="text-cream/85 text-lg max-w-md">Group departures, honeymoons and private tours across Tbilisi, Kazbegi, Gudauri, Batumi and Kakheti.</T>
          <button onClick={() => openEnquiry({ destination: "Georgia" })} data-testid="georgia-hero-enquire-btn"
            className="shrink-0 rounded-full bg-terra text-paper px-7 py-4 font-semibold hover:bg-[#a5370a] transition-colors"><T k="georgia.hero.cta">Plan my Georgia trip</T></button>
          <Link to="/georgia/visa-guide" data-testid="georgia-hero-visa-link" className="shrink-0 underline underline-offset-[6px] decoration-gold hover:text-gold transition-colors"><T k="georgia.hero.visa">Visa &amp; best time guide</T></Link>
        </motion.div>
        <TrustBadges className="mt-8" />
      </div>
    </section>
  );
}

export default function Georgia() {
  useSeo({
    title: "Georgia Tour Packages from India | No. 1 Georgia Specialists",
    description: "Georgia tour packages from Delhi: Tbilisi, Kazbegi, Gudauri, Batumi and Kakheti. Group departures, honeymoons and private tours with Georgia e-visa help from Trippy Tastic.",
    path: "/georgia",
  });
  const { trips } = useTrips();
  const georgia = trips.filter((t) => t.destination === "Georgia");
  return (
    <main data-testid="georgia-page">
      <GeorgiaHero />
      <section className="bg-paper py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead k="georgia.why" eyebrow="Why Georgia" title={["Small country.", "Enormous trip."]} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 rounded-[28px] overflow-hidden">
            {REASONS.map(([t, d], i) => (
              <Reveal key={i} delay={i * 0.08} className="bg-paper p-8 min-h-[220px] flex flex-col justify-between group hover:bg-cream transition-colors duration-500">
                <span className="font-serif italic text-5xl text-terra">0{i + 1}</span>
                <div><T k={`georgia.why.${i}.title`} as="h3" className="font-display text-xl font-bold">{t}</T><T k={`georgia.why.${i}.text`} as="p" className="mt-2 text-stone-600">{d}</T></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <RefusalStory />
      <section className="bg-wine text-cream py-24 sm:py-32 grain relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead light k="georgia.places-head" eyebrow="Where we'll take you" title={["Five places,", "one loop."]} />
          <PlacesExplorer />
        </div>
      </section>
      <section className="bg-cream py-24 sm:py-32" data-testid="georgia-trips-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead k="georgia.trips" eyebrow="Departures" title={["Georgia trips", "you can book."]} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {georgia.map((t, i) => <Reveal key={t.id} delay={(i % 3) * 0.08}><TripCard trip={t} /></Reveal>)}
          </div>
        </div>
      </section>
      <CallbackSection k="georgia.callback" preset={{ destination: "Georgia" }} title={["Let's plan", "your Georgia."]} />
    </main>
  );
}
